<template>
  <section :class="{ 'flex flex-col': !isCheckoutDesktopFlow }">
    <StatusBar v-if="!isCheckoutDesktopFlow" />
    <section :class="{ 'flex flex-col h-full grow px-4': !isCheckoutDesktopFlow }">
      <h1 class="text-2xl md:text-4xl px-4 font-bold font-heading">How many guests?</h1>
      <div
        v-if="!isLoadingTable"
        class="grid grid-cols-1 xs:grid-cols-3 xs:grid-rows-2 gap-3 sm:gap-4 lg:gap-x-8 my-6 px-4"
      >
        <template v-if="!buttonOptions">
          <ButtonDefault
            v-for="count in settingStore.max_guests"
            :key="count"
            class="px-1 text-sm md:text-base py-2 rounded font-button font-semibold w-full bg-button-guest-primary text-button-label transition-opacity hover:opacity-90"
            @click="setGuestCount(count)"
            :id="`guests-${count}`"
            :label="guestCountLabel(count)"
            :disabled="!canGuestCountBeSet(count)"
            guest
          />
        </template>
        <template v-else>
          <ButtonDefault
            v-for="count in 5"
            :key="count"
            class="px-1 text-sm md:text-base py-2 rounded font-button font-semibold w-full bg-button-guest-primary text-button-label transition-opacity hover:opacity-90"
            @click="setGuestCount(count)"
            :id="`guests-${count}`"
            :label="guestCountLabel(count)"
            guest
            :disabled="!canGuestCountBeSet(count)"
          />
          <ButtonOptions
            id="set-max-guests-button-option"
            class="w-full rounded font-semibold font-button"
            :modelValue="'6'"
            :options="maxSeatingRange"
            :buttonRules="{
              '6': { when: '6', then: `6 - ${settingStore.max_guests}`, end: '6' },
            }"
            @update:modelValue="setGuestCount"
            defaultOpensNav
            guest
            itemValue="value"
            itemText="label"
            :disabled="isButtonGroupDisable"
          />
        </template>
      </div>
      <div v-else class="flex flex-wrap my-4 animate-pulse">
        <div v-for="count in settingStore.max_guests" :key="count" class="w-1/3 px-4">
          <div
            class="my-2 px-1 text-sm md:text-base py-2 rounded font-button font-semibold w-full bg-base-secondary text-button-label transition-opacity hover:opacity-90"
          >
            <span class="invisible">{{ count }}</span>
          </div>
        </div>
      </div>
      <div class="text-right font-sans">
        More than {{ settingStore.max_guests }}? Please
        <a id="contactUsLink" class="more underline" :href="`mailto:${settingStore.reply_to}`"
          >contact us</a
        >.
      </div>
      <div class="mt-2 px-4" :class="isCheckoutDesktopFlow ? 'mt-96' : 'mt-auto pb-10'">
        <ButtonDefault
          label="Back"
          class="font-semibold uppercase text-sm px-4 py-1.5 border border-solid border-button-guest-primary rounded font-button text-button-guest-primary"
          guest
          @click="onBack"
        />
      </div>
    </section>
  </section>
</template>

<style scoped>
/* Vuetify Overrride on Anchors */
a.more {
  color: var(--secondary-theme-color) !important;
}
</style>

<script lang="ts">
import * as _ from 'lodash-es'
import { mapStores } from 'pinia'
import { defineComponent } from 'vue'

import ButtonDefault from '@/common/components/atoms/ButtonDefault.vue'
import ButtonOptions from '@/common/components/organisms/ButtonOptions.vue'
import { usePerformanceStore } from '@/modules/performance/stores/PerformanceStore'
import StatusBar from '@/pages/guest/StatusBar.vue'
import { useCheckoutStore } from '@/stores/checkout'
import { useSettingStore } from '@/stores/setting'
import { blankError } from '@/utils/errors'

import { FeatureFlag, PerformanceTableRead } from '@generated/types'

interface GuestRange {
  min: number
  max: number
}

export default defineComponent({
  name: 'SetGuests',
  components: {
    ButtonDefault,
    ButtonOptions,
    StatusBar,
  },
  data() {
    return {
      guestCount: 0,
      prevRoute: null,
      isLoadingTable: true,
      ui: {
        error: blankError(),
      },
    }
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      // @ts-ignore
      vm.prevRoute = from
    })
  },

  async mounted() {
    this.checkoutStore.clearNumberOfGuests()
    await this.checkoutStore.refreshPerformance()
    this.isLoadingTable = false
  },

  computed: {
    ...mapStores(useCheckoutStore, useSettingStore, usePerformanceStore),

    buttonOptions(): boolean {
      return this.settingStore.max_guests > 6
    },

    maxSeatingRange(): { value: string; label: string; disabled: boolean; classes: object }[] {
      let result: { value: string; label: string; disabled: boolean; classes: object }[] = []
      for (let i = 6; i <= this.settingStore.max_guests; i++) {
        result.push({
          value: i.toString(),
          label: this.guestCountLabel(i),
          disabled: !this.canGuestCountBeSet(i),
          classes: { disabled: !this.canGuestCountBeSet(i) },
        })
      }
      return result
    },

    availableTables(): PerformanceTableRead[] {
      return (this.checkoutStore.performance!.tables || []).filter(table => table.available)
    },

    enforceMinimums(): boolean {
      return this.settingStore.flagEnabled(FeatureFlag.ENFORCE_MINIMUMS)
    },

    isCheckoutDesktopFlow(): boolean {
      return this.checkoutStore.isDesktopFlow
    },

    isButtonGroupDisable(): boolean {
      return _.range(6, this.settingStore.max_guests + 1).every(i => !this.canGuestCountBeSet(i))
    },

    achievableGuestCounts(): boolean[] {
      const guestRangeFromRegularTables: GuestRange[] = this.availableTables
        .filter(table => !table.standing_room)
        .map(table => ({
          min: table.minimum_guests,
          max: table.available_guests,
        }))

      const canAchieve = new Array(this.settingStore.max_guests).fill(false)
      canAchieve[0] = true

      for (let range of guestRangeFromRegularTables) {
        for (let i = this.settingStore.max_guests - 1; i >= 0; i--) {
          if (canAchieve[i]) {
            for (
              let value = range.min;
              value <= Math.min(range.max, this.settingStore.max_guests - i);
              value++
            ) {
              canAchieve[i + value] = true
            }
          }
        }
      }

      return canAchieve
    },
  },

  methods: {
    async setGuestCount(count) {
      await this.checkoutStore.setNumberOfGuests(Number(count), this.ui.error)
    },

    onBack() {
      history.go(-1)
    },

    canGuestCountBeSet(numberOfGuests: number): boolean {
      // First, check if any standing room table can accommodate all guests
      const suitableStandingRoom = this.availableTables.find(
        table =>
          table.standing_room &&
          table.available_guests >= numberOfGuests &&
          (!this.enforceMinimums || table.minimum_guests <= numberOfGuests),
      )
      if (suitableStandingRoom) return true

      // Next, check if any regular table can accommodate all guests
      const availableRegularTables = this.availableTables.filter(table => !table.standing_room)
      if (availableRegularTables.length === 0) return false // No suitable arrangement found
      const lowestMinimum = Math.min(...availableRegularTables.map(table => table.minimum_guests))
      if (lowestMinimum > numberOfGuests) return false // No suitable arrangement found

      if (!this.enforceMinimums) {
        const availableSeats = _.sum(availableRegularTables.map(table => table.available_guests))
        return availableSeats >= numberOfGuests
      } else {
        // Finally, check if the target sum can be achieved by any combination of regular tables
        return this.achievableGuestCounts[numberOfGuests]
      }
    },

    guestCountLabel(count: number): string {
      if (!this.canGuestCountBeSet(count)) {
        return `${count} (SOLD OUT)`
      } else {
        return `${count}`
      }
    },
  },
})
</script>
